import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Stethoscope } from "lucide-react";
import { Patients, isNotFound } from "@/lib/api";
import { useDraft, readDraft, clearDraft } from "@/lib/draft";
import LoadError from "@/components/LoadError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import BodyMap from "@/components/BodyMap";
import Disclaimer from "@/components/Disclaimer";

const FIELDS = [
  { key: "chief_complaint",     label: "Queixa principal",            rows: 2, ph: "Ex.: dor lombar há 3 meses, intermitente…" },
  { key: "aggravating_factors", label: "Fatores agravantes",          rows: 2, ph: "Movimentos, posturas ou contextos que agravam." },
  { key: "relieving_factors",   label: "Fatores de alívio",           rows: 2, ph: "O que reduz os sintomas (calor, repouso, alongamento…)." },
  { key: "previous_injuries",   label: "Lesões anteriores",           rows: 2, ph: "Entorses, fraturas, traumatismos…" },
  { key: "surgeries",           label: "Cirurgias",                   rows: 2, ph: "Tipo, ano, lateralidade." },
  { key: "chronic_conditions",  label: "Condições crónicas",          rows: 2, ph: "Hipertensão, diabetes, hipotiroidismo…" },
  { key: "medication",          label: "Medicação habitual",          rows: 2, ph: "Princípios ativos, dose, frequência." },
  { key: "lifestyle",           label: "Estilo de vida",              rows: 3, ph: "Atividade física, padrão de sono, stress, hidratação, ergonomia." },
];

export default function IntakePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [initialJson, setInitialJson] = useState(null);
  const draftKey = `intake:${id}`;
  useDraft(draftKey, form, !!form && initialJson !== null && JSON.stringify(form) !== initialJson);

  useEffect(() => {
    (async () => {
      setLoadErr(false);
      try {
        const p = await Patients.get(id);
        setPatient(p);
        const draft = readDraft(draftKey);
        if (draft) toast.info("Rascunho não guardado recuperado. Reveja e clique em Guardar.", { id: "draft-restored" });
        const server = {
          gender: p.gender || "neutro",
          occupation: p.occupation || "",
          // Vitais / perfil expandido
          birth_date: p.birth_date || "",
          height_cm: p.height_cm ?? "",
          weight_kg: p.weight_kg ?? "",
          dominant_side: p.dominant_side || "",
          physical_activity_level: p.physical_activity_level || "",
          sleep_quality: p.sleep_quality || "",
          stress_level: p.stress_level || "",
          hydration_level: p.hydration_level || "",
          occupation_type: p.occupation_type || "",
          // Anamnese
          chief_complaint: p.chief_complaint || "",
          onset: p.onset || "",
          pain_locations: p.pain_locations || [],
          aggravating_factors: p.aggravating_factors || "",
          relieving_factors: p.relieving_factors || "",
          previous_injuries: p.previous_injuries || "",
          surgeries: p.surgeries || "",
          chronic_conditions: p.chronic_conditions || "",
          medication: p.medication || "",
          lifestyle: p.lifestyle || "",
        };
        setInitialJson(JSON.stringify(server));
        setForm(draft || server);
      } catch (e) {
        if (!isNotFound(e)) { setLoadErr(true); return; }
        toast.error("Paciente não encontrado");
        nav("/");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav, reloadKey]);

  if (loadErr && !form) return <LoadError testId="intake-load-error" onRetry={() => setReloadKey(k => k + 1)} />;
  if (!patient || !form) return <div className="text-slate-500" data-testid="intake-loading">A carregar…</div>;

  // IMC ao vivo (autoritativo no backend, mas damos preview ao terapeuta).
  // Robusto: auto-converte metros→cm, valida ranges humanos.
  const liveBmi = (() => {
    let h = parseFloat(form.height_cm);
    const w = parseFloat(form.weight_kg);
    if (!h || !w || h <= 0 || w <= 0) return null;
    if (h < 3) h = h * 100;                            // 1.84 m → 184 cm
    if (h < 50 || h > 280 || w < 10 || w > 400) return null;
    const bmi = w / Math.pow(h / 100, 2);
    if (bmi < 5 || bmi > 200) return null;
    return Math.round(bmi * 10) / 10;
  })();

  const save = async (goNext) => {
    if (!form.gender || !["masculino", "feminino", "neutro"].includes(form.gender)) {
      toast.error("Seleciona o género do paciente — campo obrigatório.");
      return;
    }
    setBusy(true);
    try {
      // Normaliza numéricos: enviar number ou null (não string vazia).
      // Auto-converte altura em metros para cm se o user escreveu "1.84" em vez de "184".
      let h = form.height_cm === "" ? null : Number(form.height_cm);
      if (h != null && h > 0 && h < 3) h = Math.round(h * 100);
      const payload = {
        ...form,
        height_cm: h,
        weight_kg: form.weight_kg === "" ? null : Number(form.weight_kg),
      };
      await Patients.intake(id, payload);
      setInitialJson(JSON.stringify(form));
      clearDraft(draftKey);
      toast.success("Perfil clínico atualizado");
      if (goNext) nav(`/patients/${id}/assessments/new`);
    } catch (e) {
      toast.error("Não foi possível guardar o perfil");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="intake-page">
      <Link to={`/patients/${id}`} className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar ao paciente
      </Link>

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">Perfil clínico</div>
        <h1 className="font-display text-4xl text-slate-50 mt-2">{patient.name}</h1>
        <p className="text-slate-400 text-sm mt-1">Anamnese estruturada e historial — base para a avaliação funcional.</p>
      </div>

      {/* Bloco 1 — Identificação extra */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="text-slate-300">Género <span className="text-rose-400">*</span></Label>
          <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
            <SelectTrigger className="mt-1 bg-slate-900 border-slate-800 text-slate-50" data-testid="select-gender">
              <SelectValue placeholder="Seleciona…" />
            </SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
              <SelectItem value="masculino">Masculino</SelectItem>
              <SelectItem value="feminino">Feminino</SelectItem>
              <SelectItem value="neutro">Neutro / Prefiro não dizer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="occupation" className="text-slate-300">Profissão</Label>
          <Input
            id="occupation" data-testid="input-occupation"
            value={form.occupation}
            onChange={(e) => setForm({ ...form, occupation: e.target.value })}
            className="mt-1 bg-slate-900 border-slate-800 text-slate-50"
            placeholder="Ex.: programador, atleta, motorista…"
          />
        </div>
      </section>

      {/* Bloco 1.5 — Vitais & Perfil expandido */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 space-y-5" data-testid="vitals-section">
        <div className="flex items-baseline justify-between">
          <h3 className="font-display text-lg text-slate-100">Vitais & Perfil</h3>
          {liveBmi != null && (
            <span data-testid="live-bmi" className="text-xs px-2 py-0.5 rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-200">
              IMC {liveBmi}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="birth_date" className="text-slate-300">Data de nascimento</Label>
            <Input
              id="birth_date" type="date" data-testid="form-birth-date"
              value={form.birth_date}
              onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
              className="mt-1 bg-slate-900 border-slate-800 text-slate-50"
            />
          </div>
          <div>
            <Label htmlFor="height_cm" className="text-slate-300">Altura (cm)</Label>
            <Input
              id="height_cm" type="number" min="0" step="0.1" data-testid="form-height-cm"
              value={form.height_cm}
              onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
              className="mt-1 bg-slate-900 border-slate-800 text-slate-50"
              placeholder="170"
            />
          </div>
          <div>
            <Label htmlFor="weight_kg" className="text-slate-300">Peso (kg)</Label>
            <Input
              id="weight_kg" type="number" min="0" step="0.1" data-testid="form-weight-kg"
              value={form.weight_kg}
              onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
              className="mt-1 bg-slate-900 border-slate-800 text-slate-50"
              placeholder="70"
            />
          </div>
          <div>
            <Label className="text-slate-300">Lateralidade dominante</Label>
            <Select value={form.dominant_side || ""} onValueChange={(v) => setForm({ ...form, dominant_side: v })}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-800 text-slate-50" data-testid="form-dominant-side">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                <SelectItem value="direito">Direito</SelectItem>
                <SelectItem value="esquerdo">Esquerdo</SelectItem>
                <SelectItem value="ambidextro">Ambidextro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Atividade física</Label>
            <Select value={form.physical_activity_level || ""} onValueChange={(v) => setForm({ ...form, physical_activity_level: v })}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-800 text-slate-50" data-testid="form-activity">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                <SelectItem value="sedentario">Sedentário</SelectItem>
                <SelectItem value="leve">Leve</SelectItem>
                <SelectItem value="moderado">Moderado</SelectItem>
                <SelectItem value="intenso">Intenso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Qualidade do sono</Label>
            <Select value={form.sleep_quality || ""} onValueChange={(v) => setForm({ ...form, sleep_quality: v })}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-800 text-slate-50" data-testid="form-sleep">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                <SelectItem value="ma">Má</SelectItem>
                <SelectItem value="regular">Regular</SelectItem>
                <SelectItem value="boa">Boa</SelectItem>
                <SelectItem value="excelente">Excelente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Nível de stress</Label>
            <Select value={form.stress_level || ""} onValueChange={(v) => setForm({ ...form, stress_level: v })}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-800 text-slate-50" data-testid="form-stress">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                <SelectItem value="baixo">Baixo</SelectItem>
                <SelectItem value="moderado">Moderado</SelectItem>
                <SelectItem value="alto">Alto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Hidratação</Label>
            <Select value={form.hydration_level || ""} onValueChange={(v) => setForm({ ...form, hydration_level: v })}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-800 text-slate-50" data-testid="form-hydration">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                <SelectItem value="baixa">Baixa</SelectItem>
                <SelectItem value="adequada">Adequada</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-slate-300">Tipo de ocupação</Label>
            <Select value={form.occupation_type || ""} onValueChange={(v) => setForm({ ...form, occupation_type: v })}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-800 text-slate-50" data-testid="form-occupation-type">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                <SelectItem value="sedentaria">Sedentária (sentado quase todo o dia)</SelectItem>
                <SelectItem value="em_pe">Em pé (longos períodos)</SelectItem>
                <SelectItem value="fisica">Fisicamente exigente</SelectItem>
                <SelectItem value="mista">Mista</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Bloco 2 — Mapa de dor + Início + Queixa */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BodyMap
          value={form.pain_locations}
          onChange={(v) => setForm({ ...form, pain_locations: v })}
        />
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Início</Label>
            <Select value={form.onset || ""} onValueChange={(v) => setForm({ ...form, onset: v })}>
              <SelectTrigger className="mt-1 bg-slate-900 border-slate-800 text-slate-50" data-testid="select-onset">
                <SelectValue placeholder="Seleciona…" />
              </SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 text-slate-100">
                <SelectItem value="agudo">Agudo (&lt; 6 semanas)</SelectItem>
                <SelectItem value="cronico">Crónico (&gt; 12 semanas)</SelectItem>
                <SelectItem value="recorrente">Recorrente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="chief_complaint" className="text-slate-300">Queixa principal</Label>
            <Textarea
              id="chief_complaint" data-testid="input-chief-complaint"
              rows={3}
              value={form.chief_complaint}
              onChange={(e) => setForm({ ...form, chief_complaint: e.target.value })}
              className="mt-1 bg-slate-900 border-slate-800 text-slate-50"
              placeholder="Descrição em linguagem do paciente…"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="agg" className="text-slate-300">Fatores agravantes</Label>
              <Textarea id="agg" rows={2} data-testid="input-aggravating"
                value={form.aggravating_factors}
                onChange={(e) => setForm({ ...form, aggravating_factors: e.target.value })}
                className="mt-1 bg-slate-900 border-slate-800 text-slate-50" />
            </div>
            <div>
              <Label htmlFor="rel" className="text-slate-300">Fatores de alívio</Label>
              <Textarea id="rel" rows={2} data-testid="input-relieving"
                value={form.relieving_factors}
                onChange={(e) => setForm({ ...form, relieving_factors: e.target.value })}
                className="mt-1 bg-slate-900 border-slate-800 text-slate-50" />
            </div>
          </div>
        </div>
      </section>

      {/* Bloco 3 — Historial */}
      <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        {FIELDS.filter(f => !["chief_complaint", "aggravating_factors", "relieving_factors"].includes(f.key)).map(f => (
          <div key={f.key}>
            <Label htmlFor={f.key} className="text-slate-300">{f.label}</Label>
            <Textarea
              id={f.key} data-testid={`input-${f.key}`}
              rows={f.rows}
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="mt-1 bg-slate-900 border-slate-800 text-slate-50"
              placeholder={f.ph}
            />
          </div>
        ))}
      </section>

      <div className="flex flex-wrap gap-3 justify-end">
        <Button variant="ghost" className="text-slate-400" onClick={() => nav(`/patients/${id}`)}>Cancelar</Button>
        <Button data-testid="save-intake-btn" onClick={() => save(false)} disabled={busy} className="rounded-full border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-100">
          <Save className="w-4 h-4 mr-2" /> Guardar
        </Button>
        <Button data-testid="save-and-assess-btn" onClick={() => save(true)} disabled={busy} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6 glow-sky">
          <Stethoscope className="w-4 h-4 mr-2" /> Guardar e iniciar avaliação
        </Button>
      </div>
    </div>
  );
}
